import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { differenceInDays, addMonths, addYears, format, startOfMonth } from 'date-fns';
import {
  ResponsiveContainer, ComposedChart, XAxis, YAxis, CartesianGrid,
  Line, Tooltip, ReferenceLine, Legend,
} from 'recharts';
import {
  Bitcoin, ArrowUp, Target, Calculator, Zap, Activity, Sparkles,
  Settings, Info, TrendingUp, AlertCircle, RefreshCw,
} from 'lucide-react';
import useMarketData from '../hooks/useMarketData.js';

// ─── Power Law Model Constants ───────────────────────────────────────────────
// Santostasi / Burger framework — exponents tuned for log-log fit since genesis.
// Exposed as named constants so they can be tweaked or A/B-tested later.
const GENESIS = new Date('2009-01-03T00:00:00Z');
const MODEL = {
  floor:   { coef: -17.668, exp: 5.84 }, // 10^(coef + exp·log10(days))
  fair:    { coef: -17.010, exp: 5.84 },
  ceiling: { coef: -16.344, exp: 5.84 },
};
// Spec-supplied alternative formulas — provided as a swap-in if preferred.
// Activate by passing modelVariant="spec" prop. Uses 10^coef × days^exp.
const SPEC_MODEL = {
  floor:   { coef: -17.3, exp: 5.80 },
  fair:    { coef: -17.0, exp: 5.83 },
  ceiling: { coef: -16.5, exp: 5.90 },
};

const REFRESH_INTERVAL_MS = 60_000;

// ─── Pure math utilities ─────────────────────────────────────────────────────
function daysSinceGenesis(now = new Date()) {
  return Math.max(1, differenceInDays(now, GENESIS));
}

function powerLaw({ coef, exp }, days) {
  // Two equivalent forms — we accept either parameterisation:
  //   10^(coef + exp · log10(days))   — used by Santostasi default
  //   10^coef × days^exp              — used by spec model
  // We always treat coef as 10^(coef) base * days^exp for stability.
  return Math.pow(10, coef) * Math.pow(days, exp);
}

function selectModel(variant) {
  return variant === 'spec' ? SPEC_MODEL : MODEL;
}

// Compute R² between the log10 of historical BTC prices and the log10 of
// the model's "fair" line at the same dates. Returns { rSquared, n }.
//
// Why log-space: power-law model is exponential in real space — directly
// comparing $1k-era prices to $100k-era prices gives a pathological R² that
// just measures "we're bigger now". Log-space R² is the standard goodness-
// of-fit measure for power-law fits and is what published power-law studies
// (Santostasi, Burger) report.
//
// We also recompute the implied β from the actual data so we can show how
// closely the *empirical* slope matches the model's declared β. If they
// diverge meaningfully, the user knows the model is drifting from reality.
function computeModelFit(btcHistory, model) {
  if (!btcHistory?.length) return null;

  // Build (log10(days_since_genesis), log10(price)) pairs from real data
  const pairs = [];
  for (const p of btcHistory) {
    if (!p?.date || !Number.isFinite(p.value) || p.value <= 0) continue;
    const days = Math.max(1, Math.floor((p.date.getTime() - GENESIS.getTime()) / 86_400_000));
    if (days <= 0) continue;
    pairs.push({ x: Math.log10(days), y: Math.log10(p.value) });
  }
  if (pairs.length < 24) return null;

  // 1) R² of the model's fair line vs the data (pinned coefficients).
  //    log10(fair) = model.fair.coef + model.fair.exp · log10(days)
  const meanY = pairs.reduce((s, p) => s + p.y, 0) / pairs.length;
  let ssRes = 0, ssTot = 0;
  for (const p of pairs) {
    const yhat = model.fair.coef + model.fair.exp * p.x;
    ssRes += (p.y - yhat) ** 2;
    ssTot += (p.y - meanY) ** 2;
  }
  const rSquared = ssTot > 0 ? 1 - (ssRes / ssTot) : 0;

  // 2) Empirical slope (β) from least-squares fit of the same data —
  //    surfaced alongside the model's declared β so divergence is visible.
  const meanX = pairs.reduce((s, p) => s + p.x, 0) / pairs.length;
  let num = 0, den = 0;
  for (const p of pairs) {
    num += (p.x - meanX) * (p.y - meanY);
    den += (p.x - meanX) ** 2;
  }
  const empiricalBeta = den > 0 ? num / den : null;

  return {
    rSquared,
    declaredBeta: model.fair.exp,
    empiricalBeta,
    n: pairs.length,
    firstDate: btcHistory[0]?.date,
    lastDate: btcHistory[btcHistory.length - 1]?.date,
  };
}

// ─── Tick clock — drives "live now" mathematics on a 60s heartbeat ──────────
function useNow(intervalMs = REFRESH_INTERVAL_MS) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function PowerLawNavigator({
  modelVariant = 'santostasi',  // 'santostasi' | 'spec'
  className = '',
}) {
  const [mode, setMode] = useState('simple');
  const [investment, setInvestment] = useState(1000);
  const [showFormulas, setShowFormulas] = useState(false);

  // Subscribe to the shared market-data hook instead of running a parallel
  // CoinGecko/CoinCap polling loop. Two timers hammering the same upstream
  // is what triggered the rate-limit / CORS-blocked errors in the browser
  // console; one shared subscription is enough.
  const { btc, gold, refetch: marketRefetch } = useMarketData();
  const { spot: price, history: btcHistory, status, error, fetchedAt } = btc;
  const refetch = marketRefetch.btc;

  // Denominator toggle for the projection chart only — USD by default,
  // optionally "ounces of gold" so users can read BTC priced in real money.
  // Tile values across the rest of the section stay in USD because that's
  // what most users expect for "today's price"; only the long-form chart
  // gets the toggle.
  const [denominator, setDenominator] = useState('usd'); // 'usd' | 'gold'
  const now = useNow();
  const model = useMemo(() => selectModel(modelVariant), [modelVariant]);

  // Model fit quality (R²) computed from real BTC history vs the fair-value
  // line. R² near 1.0 means the power law is a strong fit; lower numbers
  // mean recent price action is decoupling from the long-run trajectory.
  // β (the exponent) is just the model constant — we surface it because
  // it's what gives the model its "power-law" character; comparing it
  // across rival fits (Santostasi β=5.84, bitcoinpower.law β≈5.68) tells
  // the user which curve they're looking at.
  const modelFit = useMemo(() => {
    if (!btcHistory?.length) return null;
    return computeModelFit(btcHistory, model);
  }, [btcHistory, model]);

  // ─── Today's model levels ────────────────────────────────────────
  const todayLevels = useMemo(() => {
    const d = daysSinceGenesis(now);
    return {
      days: d,
      floor:   powerLaw(model.floor,   d),
      fair:    powerLaw(model.fair,    d),
      ceiling: powerLaw(model.ceiling, d),
    };
  }, [model, now]);

  // ─── Derived metrics ────────────────────────────────────────────
  const metrics = useMemo(() => {
    if (!price) return null;
    const { floor, fair, ceiling } = todayLevels;

    // Logarithmic position — non-linear interpolation per spec
    const logPos = Math.max(0, Math.min(100,
      ((Math.log(price) - Math.log(floor)) /
       (Math.log(ceiling) - Math.log(floor))) * 100
    ));

    // Linear fallback — used in some labels because it's more intuitive
    const linearPos = Math.max(0, Math.min(100,
      ((price - floor) / (ceiling - floor)) * 100
    ));

    return {
      logPos, linearPos,
      distanceToFloor:    ((price - floor) / floor) * 100,         // % above floor
      distanceToFair:     ((fair - price) / fair) * 100,           // % discount to fair
      distanceToCeiling:  ((ceiling - price) / price) * 100,       // % upside to ceiling
      multiplierToFair:    fair    / price,
      multiplierToCeiling: ceiling / price,
      gapToFairUSD: fair - price,
      // Status zone — derived from logarithmic channel position
      ...zoneFor(logPos),
    };
  }, [price, todayLevels]);

  // ─── 12-month projection series for the chart ───────────────────
  // Builds a monthly time series from 12 months ago to 12 months ahead.
  // Live BTC value is plotted as a single point at "now"; the model
  // continues forward as a projected line.
  const chartSeries = useMemo(() => {
    // For gold-denominated mode, divide each level by gold price at that
    // moment in time:
    //   - Past months: use the matching gold history close (real ratio)
    //   - "Now" + future: use today's spot gold (we can't predict gold)
    // The discontinuity at "now" is the honest disclosure that future
    // BTC/gold ratios assume gold stays at today's price.
    const useGold = denominator === 'gold';
    const goldNow = gold?.spot ?? null;
    // Build YYYY-MM -> gold close map for historical lookups
    const goldByMonth = new Map();
    if (useGold && Array.isArray(gold?.history)) {
      for (const p of gold.history) {
        if (!p?.date || !Number.isFinite(p.value)) continue;
        const key = `${p.date.getFullYear()}-${p.date.getMonth()}`;
        goldByMonth.set(key, p.value);
      }
    }
    const goldAt = (d, isFuture) => {
      if (!useGold) return 1;
      if (isFuture || !goldByMonth.size) return goldNow ?? null;
      const k = `${d.getFullYear()}-${d.getMonth()}`;
      return goldByMonth.get(k) ?? goldNow ?? null;
    };

    const series = [];
    const startMonth = startOfMonth(addMonths(now, -12));
    for (let i = 0; i <= 24; i++) {
      const d = addMonths(startMonth, i);
      const days = daysSinceGenesis(d);
      const isFuture = d.getTime() > now.getTime();
      const divisor = goldAt(d, isFuture);
      // If we asked for gold mode but haven't loaded gold yet, fall back to
      // USD for this row rather than rendering NaN. The toggle UI shows a
      // skeleton when divisor is unavailable so the user knows to wait.
      const div = useGold && Number.isFinite(divisor) && divisor > 0 ? divisor : 1;
      series.push({
        date: format(d, 'MMM yy'),
        ts: d.getTime(),
        floor:    powerLaw(model.floor,   days) / div,
        fair:     powerLaw(model.fair,    days) / div,
        ceiling:  powerLaw(model.ceiling, days) / div,
        btc:      (!isFuture && price && Math.abs(d - now) < 32 * 86400 * 1000)
                  ? price / div : null,
      });
    }
    return series;
  }, [model, now, price, denominator, gold?.spot, gold?.history]);

  // PowerLawNavigator was originally a standalone page; the min-h-screen +
  // p-4/md:p-8 wrapper duplicates padding the App's <main> already applies.
  // Keep the font-sans class (this section uses Inter) but let the outer
  // App container own layout.
  return (
    <div className={`text-slate-100 font-sans ${className}`}>
      <div className="space-y-6">
        <Header
          mode={mode} setMode={setMode}
          status={status} fetchedAt={fetchedAt} onRefresh={refetch}
        />

        {/* Error banner */}
        {status === 'error' && !price && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-red-300">Unable to load Bitcoin price</div>
              <div className="text-sm text-slate-300 mt-1">{error}</div>
              <button onClick={refetch} className="mt-3 text-xs px-3 py-1.5 rounded-md bg-red-500/15 border border-red-500/30 text-red-300 hover:bg-red-500/20 transition-colors">
                Retry
              </button>
            </div>
          </div>
        )}

        {status === 'stale' && price && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-sm text-amber-300 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Price feed unavailable — showing last known value from {fetchedAt && format(fetchedAt, 'p')}
          </div>
        )}

        {/* Loading skeleton */}
        {status === 'loading' && !price && <SkeletonLoader />}

        {/* Main content — only renders once price is known */}
        {price && metrics && (
          <>
            <StatusBadge metrics={metrics} />
            {modelFit && <ModelConfidence fit={modelFit} model={model} modelVariant={modelVariant} />}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5">
                <UpsideRunway
                  metrics={metrics} levels={todayLevels} price={price}
                />
              </div>
              <div className="lg:col-span-7 space-y-6">
                <FairValueGap metrics={metrics} levels={todayLevels} price={price} />
                <WhatIfCalculator
                  investment={investment} setInvestment={setInvestment}
                  metrics={metrics}
                />
              </div>
            </div>

            {/* Live projection chart */}
            <ProjectionChart
              series={chartSeries} price={price} now={now}
              denominator={denominator}
              setDenominator={setDenominator}
              goldSpot={gold?.spot}
              goldStatus={gold?.status}
            />

            {/* Multi-horizon target prices */}
            <HorizonTimeline model={model} now={now} price={price} />

            {/* Pro mode only */}
            {mode === 'pro' && (
              <ProModeStats
                metrics={metrics} levels={todayLevels} model={model}
                modelVariant={modelVariant}
                showFormulas={showFormulas} setShowFormulas={setShowFormulas}
                now={now} price={price}
              />
            )}
          </>
        )}

        <Footer />
      </div>
    </div>
  );
}

// ─── Zone classification ────────────────────────────────────────────────────
function zoneFor(logPos) {
  if (logPos < 15) return {
    zone: 'ACCUMULATION PHASE', zoneColor: 'green',
    zoneText: 'Historically, buying in this zone has led to maximum long-term returns.',
  };
  if (logPos < 35) return {
    zone: 'FAIR VALUE ZONE', zoneColor: 'blue',
    zoneText: 'Bitcoin is fairly valued by historical standards.',
  };
  if (logPos < 65) return {
    zone: 'PREMIUM ZONE', zoneColor: 'amber',
    zoneText: 'Caution warranted — well above mathematical fair value.',
  };
  return {
    zone: 'EUPHORIA / TOP RISK', zoneColor: 'red',
    zoneText: 'Historically a poor time to add — cycle top risk elevated.',
  };
}

// ─── Header ─────────────────────────────────────────────────────────────────
function Header({ mode, setMode, status, fetchedAt, onRefresh }) {
  return (
    <header className="flex items-start justify-between flex-wrap gap-4">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-lg shadow-orange-500/20">
          <Bitcoin className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Power Law Navigator</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm text-slate-400">Where Bitcoin sits in its long-term mathematical channel</p>
            <LiveStatusPill status={status} fetchedAt={fetchedAt} onRefresh={onRefresh} />
          </div>
        </div>
      </div>
      <div className="flex items-center bg-slate-900 rounded-xl p-1 border border-slate-800">
        <ModeButton active={mode === 'simple'} onClick={() => setMode('simple')} icon={Sparkles} label="Simple" />
        <ModeButton active={mode === 'pro'}    onClick={() => setMode('pro')}    icon={Settings}  label="Pro" />
      </div>
    </header>
  );
}

function LiveStatusPill({ status, fetchedAt, onRefresh }) {
  const [_tick, setTick] = useState(0);
  // Re-render every 10s so "X seconds ago" stays current
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10_000);
    return () => clearInterval(id);
  }, []);
  const ago = fetchedAt ? Math.floor((Date.now() - fetchedAt.getTime()) / 1000) : null;
  const agoLabel = ago == null ? '—'
    : ago < 60 ? `${ago}s ago`
    : ago < 3600 ? `${Math.floor(ago / 60)}m ago`
    : `${Math.floor(ago / 3600)}h ago`;

  const tone = {
    live:       'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    refreshing: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    loading:    'bg-slate-500/10 border-slate-500/30 text-slate-400',
    stale:      'bg-amber-500/10 border-amber-500/30 text-amber-400',
    error:      'bg-red-500/10 border-red-500/30 text-red-400',
  }[status] || 'bg-slate-500/10 border-slate-500/30 text-slate-400';

  return (
    <button
      onClick={onRefresh}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-wider hover:opacity-80 transition-opacity ${tone}`}
      title="Click to refresh now"
    >
      <span className={`w-1.5 h-1.5 rounded-full bg-current ${status === 'live' ? 'animate-pulse' : ''}`} />
      <span>{status === 'live' ? `Live · ${agoLabel}` : status}</span>
      <RefreshCw className={`w-3 h-3 ${status === 'refreshing' ? 'animate-spin' : ''}`} />
    </button>
  );
}

function ModeButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={[
        'px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2',
        active ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200',
      ].join(' ')}
    >
      <Icon className="w-4 h-4" />{label}
    </button>
  );
}

// ─── Skeleton Loader ────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-32 rounded-2xl bg-slate-900 border border-slate-800" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 h-[520px] rounded-2xl bg-slate-900 border border-slate-800" />
        <div className="lg:col-span-7 space-y-6">
          <div className="h-48 rounded-2xl bg-slate-900 border border-slate-800" />
          <div className="h-72 rounded-2xl bg-slate-900 border border-slate-800" />
        </div>
      </div>
      <div className="h-96 rounded-2xl bg-slate-900 border border-slate-800" />
    </div>
  );
}

// ─── Status Badge ───────────────────────────────────────────────────────────
// ─── Model Confidence Strip ──────────────────────────────────────────────────
// Surfaces the math behind the prediction: declared β, empirical β derived
// from real BTC history, R² of the fit, and the lookback window. b1m.io
// pioneered this pattern and it's the right call — without these numbers,
// power-law projections read like astrology rather than a fitted regression.
function ModelConfidence({ fit, model, modelVariant }) {
  const { rSquared, declaredBeta, empiricalBeta, n, firstDate, lastDate } = fit;
  // Color the R² number by quality so readers can scan: 0.95+ "strong fit",
  // 0.85-0.95 "good", below 0.85 the model is decoupling from price action.
  const r2Tone = rSquared >= 0.95 ? 'text-emerald-300'
              : rSquared >= 0.85 ? 'text-amber-300'
              :                    'text-red-300';
  const r2Label = rSquared >= 0.95 ? 'Strong fit'
                : rSquared >= 0.85 ? 'Good fit'
                :                    'Drifting';

  // β divergence: how far the empirical slope is from the model's declared β.
  // Small divergence (< 5%) = model still describes recent reality; larger
  // divergence = model is overdue for a refit.
  const betaDivergence = Number.isFinite(empiricalBeta)
    ? Math.abs((empiricalBeta - declaredBeta) / declaredBeta) * 100
    : null;

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur p-5">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-orange-400" />
            Model Confidence
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5">
            How well the power-law fit explains real BTC price history
          </p>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono tabular-nums">
          {modelVariant} variant · {n} months ·{' '}
          {firstDate && lastDate
            ? `${format(firstDate, 'MMM yyyy')} → ${format(lastDate, 'MMM yyyy')}`
            : ''}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <ConfidenceTile
          label="R² accuracy"
          value={`${(rSquared * 100).toFixed(2)}%`}
          sub={r2Label}
          tone={r2Tone}
          tooltip="Coefficient of determination, computed in log-space (the standard for power-law fits). 1.00 = price tracks the model exactly; lower means recent action is decoupling from the long-run trajectory."
        />
        <ConfidenceTile
          label="β (declared)"
          value={declaredBeta.toFixed(3)}
          sub="Model exponent"
          tone="text-sky-300"
          tooltip="The power-law slope used by this model. Santostasi default = 5.84; bitcoinpower.law ≈ 5.68. The exponent is what gives the model its 'power-law' character."
        />
        <ConfidenceTile
          label="β (empirical)"
          value={Number.isFinite(empiricalBeta) ? empiricalBeta.toFixed(3) : '—'}
          sub="Fit from real data"
          tone={betaDivergence != null && betaDivergence < 5
            ? 'text-emerald-300'
            : 'text-amber-300'}
          tooltip="Slope obtained by least-squares fitting the loaded BTC history directly. If this number drifts far from the declared β, the model is overdue for a refit."
        />
        <ConfidenceTile
          label="β divergence"
          value={betaDivergence != null
            ? `${betaDivergence < 0.5 ? '<0.5' : betaDivergence.toFixed(1)}%`
            : '—'}
          sub={betaDivergence != null && betaDivergence < 5
            ? 'Model agrees with reality'
            : 'Empirical slope drifting'}
          tone={betaDivergence != null && betaDivergence < 5
            ? 'text-emerald-300'
            : 'text-amber-300'}
          tooltip="Percent gap between declared and empirical β. Under 5% = the published model still describes recent price action well."
        />
      </div>

      <div className="text-[10px] text-slate-500 italic mt-3 leading-relaxed">
        Computed in log-space from monthly BTC closes. Coefficients are pinned
        from {modelVariant === 'spec' ? 'the spec sheet' : 'Santostasi/Burger'} —
        we measure how well *they* fit your loaded data, not how the data refits
        them. A high R² here means the model still works; a low R² means today's
        market is in a phase the model can't yet predict.
      </div>
    </div>
  );
}

function ConfidenceTile({ label, value, sub, tone, tooltip }) {
  return (
    <div className="px-4 py-3 bg-slate-950/50 border border-white/10 rounded-lg" title={tooltip}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-2xl md:text-3xl font-bold tabular-nums mt-1 ${tone}`}>{value}</div>
      <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>
    </div>
  );
}

function StatusBadge({ metrics }) {
  const lowerBucket = metrics.logPos < 15 ? 15
                    : metrics.logPos < 25 ? 25
                    : metrics.logPos < 50 ? 50 : 100;

  const tone = {
    green:  { bg: 'from-emerald-500/15 to-green-500/5',   border: 'border-emerald-500/40', text: 'text-emerald-300', emoji: '🟢' },
    blue:   { bg: 'from-blue-500/15 to-cyan-500/5',       border: 'border-blue-500/40',    text: 'text-blue-300',    emoji: '🔵' },
    amber:  { bg: 'from-amber-500/15 to-orange-500/5',    border: 'border-amber-500/40',   text: 'text-amber-300',   emoji: '🟡' },
    red:    { bg: 'from-red-500/15 to-rose-500/5',        border: 'border-red-500/40',     text: 'text-red-300',     emoji: '🔴' },
  }[metrics.zoneColor];

  return (
    <div className={`relative overflow-hidden rounded-2xl border-2 ${tone.border} bg-gradient-to-r ${tone.bg} p-6`}>
      <div className="absolute -top-12 -right-12 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
      <div className="relative flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="text-4xl leading-none">{tone.emoji}</div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Status</div>
            <h2 className={`text-2xl md:text-3xl font-bold ${tone.text}`}>{metrics.zone}</h2>
            <p className="text-sm text-slate-300 mt-1 max-w-2xl">{metrics.zoneText}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Channel Position</div>
          <div className="text-4xl md:text-5xl font-bold text-white tabular-nums">
            {metrics.logPos.toFixed(0)}%
          </div>
          <div className={`text-xs ${tone.text} font-semibold`}>Lower {lowerBucket}% of channel (log)</div>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmtUSD = (v) => {
  if (!Number.isFinite(v)) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${Math.round(v).toLocaleString()}`;
  if (v >= 1)         return `$${v.toFixed(2)}`;
  return `$${v.toFixed(4)}`;
};

const fmtCompact = (v) => {
  if (!Number.isFinite(v)) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

// ─── Upside Runway Gauge ────────────────────────────────────────────────────
function UpsideRunway({ metrics, levels, price }) {
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-2xl p-4 md:p-6 h-full">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300 flex items-center gap-2">
          <ArrowUp className="w-4 h-4 text-orange-400" />
          Upside Runway
        </h3>
        <span className="text-xs text-slate-500 font-mono tabular-nums">
          {metrics.multiplierToCeiling.toFixed(1)}× to ceiling
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-6">How much room Bitcoin has before the cycle ceiling</p>

      <div className="flex gap-5 items-stretch h-[440px]">
        {/* Vertical gauge */}
        <div className="relative w-14 flex-shrink-0">
          <div className="absolute inset-0 rounded-full overflow-hidden bg-slate-800 border border-slate-700">
            <div className="absolute inset-x-0 bottom-0 top-0 bg-gradient-to-t from-blue-600 via-slate-500 to-orange-500" />
            {/* Fair-value tick line — uses logarithmic position to match the gauge mapping */}
            <div
              className="absolute inset-x-0 h-px bg-white/40"
              style={{
                bottom: `${((Math.log(levels.fair) - Math.log(levels.floor)) /
                           (Math.log(levels.ceiling) - Math.log(levels.floor))) * 100}%`,
              }}
            />
          </div>

          {/* Bitcoin marker — positioned by logarithmic channel position */}
          <div
            className="absolute left-1/2 -translate-x-1/2 transition-[bottom] duration-700"
            style={{ bottom: `calc(${metrics.logPos}% - 28px)` }}
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-orange-400/30 blur-xl animate-pulse" />
              <div className="relative w-14 h-14 rounded-full bg-white shadow-2xl flex items-center justify-center border-4 border-slate-950">
                <Bitcoin className="w-7 h-7 text-orange-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Labels column */}
        <div className="flex-1 flex flex-col justify-between text-xs">
          <RunwayLabel
            color="orange"
            label="Cycle Ceiling"
            value={fmtCompact(levels.ceiling)}
            sub={`${metrics.multiplierToCeiling.toFixed(1)}× from here`}
          />
          <RunwayLabel
            color="slate"
            label="Fair Value"
            value={fmtCompact(levels.fair)}
            sub={`${metrics.multiplierToFair.toFixed(2)}× from here`}
            faint
          />
          <RunwayLabel
            highlight
            label="You Are Here"
            value={fmtUSD(price)}
            sub={`+${metrics.distanceToFloor.toFixed(0)}% above floor`}
          />
          <RunwayLabel
            color="blue"
            label="Mathematical Floor"
            value={fmtCompact(levels.floor)}
            sub="Long-term support"
          />
        </div>
      </div>

      {/* Live delta strip — distance to floor / fair */}
      <div className="mt-5 pt-5 border-t border-slate-800 grid grid-cols-2 gap-3">
        <DeltaStat
          label="Distance to Floor"
          value={`+${metrics.distanceToFloor.toFixed(1)}%`}
          color="text-emerald-400"
          sub={`Floor at ${fmtCompact(levels.floor)}`}
        />
        <DeltaStat
          label="Distance to Fair Value"
          value={`${metrics.distanceToFair > 0 ? '−' : '+'}${Math.abs(metrics.distanceToFair).toFixed(1)}%`}
          color={metrics.distanceToFair > 0 ? 'text-blue-400' : 'text-orange-400'}
          sub={metrics.distanceToFair > 0 ? 'Below fair' : 'Above fair'}
        />
      </div>
    </section>
  );
}

function DeltaStat({ label, value, color, sub }) {
  return (
    <div className="bg-slate-800/50 rounded-lg px-3 py-2 border border-slate-700">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${color}`}>{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function RunwayLabel({ color, label, value, sub, highlight, faint }) {
  if (highlight) {
    return (
      <div className="bg-orange-500/10 border border-orange-500/40 rounded-lg px-3 py-2">
        <div className="text-orange-400 font-bold uppercase tracking-widest text-[10px]">{label}</div>
        <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
        <div className="text-orange-300/80 text-xs">{sub}</div>
      </div>
    );
  }
  const colorMap = { orange: 'text-orange-400', blue: 'text-blue-400', slate: 'text-slate-300' };
  return (
    <div className={faint ? 'opacity-80' : ''}>
      <div className={`${colorMap[color] || 'text-slate-300'} font-bold uppercase tracking-widest text-[10px] mb-0.5`}>{label}</div>
      <div className="text-xl font-bold text-white tabular-nums">{value}</div>
      <div className="text-slate-500 text-xs">{sub}</div>
    </div>
  );
}

// ─── Fair Value Gap ─────────────────────────────────────────────────────────
function FairValueGap({ metrics, levels, price }) {
  const fillPct = Math.min(100, (price / levels.fair) * 100);

  return (
    <section className="relative overflow-hidden bg-slate-900 border border-slate-800 rounded-2xl p-4 md:p-6">
      <div className="absolute -top-16 -right-16 w-56 h-56 bg-emerald-500/5 rounded-full blur-3xl" />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300 flex items-center gap-2">
            <Target className="w-4 h-4 text-emerald-400" />
            Fair Value Gap
          </h3>
          <span className="text-xs text-slate-500">vs. mathematical fair value</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">
              {metrics.distanceToFair > 0 ? 'Current Discount' : 'Current Premium'}
            </div>
            <div className={[
              'text-6xl font-bold tabular-nums leading-none',
              metrics.distanceToFair > 0 ? 'text-emerald-400' : 'text-orange-400',
            ].join(' ')}>
              {Math.abs(metrics.distanceToFair).toFixed(0)}%
            </div>
            <p className="text-sm text-slate-300 mt-3 leading-relaxed">
              Bitcoin is currently trading{' '}
              <span className="font-bold text-white">
                {fmtUSD(Math.abs(metrics.gapToFairUSD))}{' '}
                {metrics.distanceToFair > 0 ? 'below' : 'above'}
              </span>{' '}
              its mathematical fair value of{' '}
              <span className="font-bold text-white">{fmtCompact(levels.fair)}</span>.
            </p>
          </div>

          <div className="md:col-span-3 space-y-3">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-orange-400 font-semibold">Current</span>
              <span className="font-bold text-white tabular-nums text-lg">{fmtUSD(price)}</span>
            </div>
            <div className="relative h-4 bg-slate-800 rounded-full overflow-hidden border border-slate-700">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange-500 to-amber-400 rounded-full transition-all duration-1000"
                style={{ width: `${fillPct}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-end pr-2">
                <span className="text-[10px] font-bold text-white/80">FAIR →</span>
              </div>
            </div>
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-emerald-400 font-semibold">Fair Value</span>
              <span className="font-bold text-emerald-400 tabular-nums text-lg">{fmtCompact(levels.fair)}</span>
            </div>
            <div className="text-xs text-slate-500 pt-1">
              {fillPct.toFixed(0)}% of the way to fair value
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── What If Calculator ─────────────────────────────────────────────────────
function WhatIfCalculator({ investment, setInvestment, metrics }) {
  const btcAmount  = investment / (metrics ? 1 : 1); // computed from price below
  // Recompute using metrics-based multipliers for stability
  const fairAmount = investment * metrics.multiplierToFair;
  const ceilAmount = investment * metrics.multiplierToCeiling;

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-2xl p-4 md:p-6">
      <div className="flex items-center gap-2 mb-1">
        <Calculator className="w-4 h-4 text-amber-400" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">What If Calculator</h3>
      </div>
      <p className="text-xs text-slate-500 mb-5">How much your investment could grow if Bitcoin reaches its targets</p>

      <div className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2 font-semibold">
            Your Investment Today
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-lg">$</span>
            <input
              type="number"
              min="0"
              step="100"
              value={investment}
              onChange={(e) => setInvestment(Math.max(0, parseFloat(e.target.value) || 0))}
              className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-9 pr-4 py-3 text-white text-xl font-bold tabular-nums focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all"
            />
          </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            {[100, 1_000, 10_000, 100_000].map((v) => (
              <button
                key={v}
                onClick={() => setInvestment(v)}
                className={[
                  'px-3 py-1 rounded-lg text-xs font-medium transition-colors border',
                  investment === v
                    ? 'bg-orange-500/15 border-orange-500/50 text-orange-300'
                    : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700',
                ].join(' ')}
              >
                {fmtCompact(v)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <ProjectionCard
            label="If reaches Fair Value"
            value={fmtUSD(fairAmount)}
            multiplier={metrics.multiplierToFair}
            color="blue" icon={TrendingUp}
          />
          <ProjectionCard
            label="If reaches Cycle Ceiling"
            value={fmtUSD(ceilAmount)}
            multiplier={metrics.multiplierToCeiling}
            color="orange" icon={Zap} highlight
          />
        </div>

        <div className="text-xs text-slate-500 italic flex items-start gap-2 pt-3 border-t border-slate-800">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Projections are computed live from today's power-law levels and recalculate
            every 60 seconds with the latest BTC price. Past performance is not indicative
            of future results.
          </span>
        </div>
      </div>
    </section>
  );
}

function ProjectionCard({ label, value, multiplier, color, icon: Icon, highlight }) {
  const styles = highlight
    ? 'bg-gradient-to-br from-orange-500/15 to-amber-500/5 border-orange-500/40'
    : 'bg-slate-800/50 border-slate-700';
  const accent = color === 'orange' ? 'text-orange-400' : 'text-blue-400';

  return (
    <div className={`${styles} border rounded-xl p-4 relative overflow-hidden`}>
      {Icon && <Icon className={`absolute top-3 right-3 w-4 h-4 ${accent} opacity-40`} />}
      <div className={`text-xs uppercase tracking-wider ${accent} font-semibold`}>{label}</div>
      <div className={`text-3xl font-bold ${accent} tabular-nums mt-2 leading-none`}>{value}</div>
      <div className="text-xs text-slate-400 mt-1">
        <span className={`font-bold ${accent}`}>{multiplier.toFixed(2)}×</span> your investment
      </div>
    </div>
  );
}

// ─── Projection Chart — past 12 months + future 12 months ───────────────────
function DenomToggle({ value, onChange, goldReady, goldStatus }) {
  const goldDisabled = !goldReady;
  const isLoading = goldStatus === 'loading';
  return (
    <div
      role="tablist"
      aria-label="Chart denominator"
      className="inline-flex items-center gap-0.5 p-0.5 rounded-md bg-slate-950/70 border border-white/10"
    >
      <DenomButton
        active={value === 'usd'}
        onClick={() => onChange('usd')}
        title="Render BTC and the power-law channel in US dollars"
      >
        USD
      </DenomButton>
      <DenomButton
        active={value === 'gold'}
        disabled={goldDisabled}
        onClick={() => goldDisabled ? null : onChange('gold')}
        title={
          goldDisabled
            ? 'Waiting for gold price to load…'
            : 'Render BTC and the power-law channel in ounces of gold (BTC ÷ XAU/USD)'
        }
      >
        oz Gold {isLoading && goldDisabled && <span className="opacity-60">…</span>}
      </DenomButton>
    </div>
  );
}

function DenomButton({ active, disabled, onClick, title, children }) {
  const cls = active
    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/40'
    : disabled
    ? 'text-slate-600 cursor-not-allowed border border-transparent'
    : 'text-slate-400 hover:text-slate-200 hover:bg-white/5 border border-transparent';
  return (
    <button
      role="tab"
      aria-selected={!!active}
      disabled={!!disabled}
      onClick={onClick}
      title={title}
      className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded transition-colors ${cls}`}
    >
      {children}
    </button>
  );
}

function ProjectionChart({ series, price, now, denominator, setDenominator, goldSpot, goldStatus }) {
  const nowTs = now.getTime();
  const isGold = denominator === 'gold';
  const goldReady = Number.isFinite(goldSpot) && goldSpot > 0;
  // Format Y-axis + tooltip for the active denominator. Gold-denominated
  // BTC/oz values are typically 20-50× — small enough that fmtCompact's
  // K/M abbreviation isn't useful; format with two decimals instead.
  const fmtY = isGold
    ? (v) => Number.isFinite(v) ? `${v.toFixed(1)} oz` : '—'
    : fmtCompact;
  const denomLabel = isGold ? 'oz of gold' : 'USD';
  const btcDisplay = isGold && goldReady && Number.isFinite(price)
    ? `${(price / goldSpot).toFixed(2)} oz`
    : fmtUSD(price);

  return (
    <section className="bg-slate-900 border border-slate-800 rounded-2xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300 flex items-center gap-2">
          <Activity className="w-4 h-4 text-purple-400" />
          12-Month Projection — Past Year + Year Ahead
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Denominator toggle — USD (default) or oz-of-gold. Inspired by
              b1m.io's BTC/Gold tab, but section-scoped here rather than
              hijacking page navigation. */}
          <DenomToggle
            value={denominator}
            onChange={setDenominator}
            goldReady={goldReady}
            goldStatus={goldStatus}
          />
          <span className="text-xs text-slate-500 font-mono hidden md:inline">Live · recalculated each minute</span>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Floor / Fair / Ceiling extend forward using the power-law formulas
        {isGold && (
          <span className="text-amber-300/80">
            {' '}· divided by historical gold for past months, today's spot{' '}
            {goldReady ? `($${goldSpot.toFixed(0)}/oz)` : ''}
            {' '}for "now" and future (we can't predict gold)
          </span>
        )}
      </p>

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
            />
            <YAxis
              scale="log" domain={['dataMin', 'dataMax']}
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
              tickFormatter={fmtY}
            />
            <ReferenceLine
              x={series.find(s => s.ts >= nowTs)?.date}
              stroke="rgba(255,255,255,0.3)" strokeDasharray="4 4"
              label={{ value: 'NOW', fill: 'rgba(255,255,255,0.6)', fontSize: 10, position: 'top' }}
            />
            <Line type="monotone" dataKey="ceiling" stroke="#a78bfa" strokeWidth={1.5} dot={false} strokeDasharray="4 3" name="Cycle Ceiling" />
            <Line type="monotone" dataKey="fair"    stroke="rgba(255,255,255,0.5)" strokeWidth={1} dot={false} strokeDasharray="2 4" name="Fair Value" />
            <Line type="monotone" dataKey="floor"   stroke="#2dd4bf" strokeWidth={1.5} dot={false} strokeDasharray="4 3" name="Power Law Floor" />
            <Line type="monotone" dataKey="btc"     stroke="#f7931a" strokeWidth={3} dot={{ r: 5, stroke: '#f7931a', fill: '#0e1012', strokeWidth: 2 }} name={`BTC: ${btcDisplay}`} connectNulls={false} />
            <Tooltip
              contentStyle={{ background: 'rgba(20,22,26,0.96)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: '#e8eaec' }}
              formatter={(v) => fmtY(v)}
            />
            <Legend wrapperStyle={{ paddingTop: 8, fontSize: 11 }} iconType="line"
              formatter={(v) => <span className="text-slate-300">{v}</span>} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

// ─── Pro Mode Stats ─────────────────────────────────────────────────────────
// ─── HorizonTimeline ─────────────────────────────────────────────────────────
// Projects the model out at fixed future horizons (1Y / 5Y / 10Y / 20Y) and
// renders the floor/fair/ceiling bands at each waypoint. Borrowed from b1m.io
// but improved: their site shows a single point estimate per horizon ("$1.5M
// by Dec 2035"), which feels falsely precise. We show the band so the user
// sees the channel widening with time — that's the honest read.
//
// The model ALWAYS produces a number for any future date; the question is
// whether you should believe it. The model-confidence strip (R², empirical β
// divergence) is the user's check on that. This timeline assumes the user
// has read that and now wants to know "if the model holds, where do we go?"
function HorizonTimeline({ model, now, price }) {
  const horizons = [
    { years: 1,  label: '1 year'   },
    { years: 5,  label: '5 years'  },
    { years: 10, label: '10 years' },
    { years: 20, label: '20 years' },
  ];

  const points = horizons.map(h => {
    const d = addYears(now, h.years);
    const days = daysSinceGenesis(d);
    return {
      ...h,
      date: d,
      floor:    powerLaw(model.floor,   days),
      fair:     powerLaw(model.fair,    days),
      ceiling:  powerLaw(model.ceiling, days),
    };
  });

  // For the headline "fair × today's price" multiplier under each tile.
  const fairToday = powerLaw(model.fair, daysSinceGenesis(now));

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-orange-500/[0.04] via-slate-900/60 to-amber-500/[0.04] backdrop-blur-xl p-4 md:p-6">
      <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-300 flex items-center gap-2">
              <Target className="w-4 h-4 text-orange-400" />
              Long-Range Targets
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Power-law projections at 1, 5, 10, and 20 years from today
            </p>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
            From {format(now, 'MMM d, yyyy')}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {points.map(p => {
            const fairMultiple = price ? p.fair / price : null;
            const ceilingMultiple = price ? p.ceiling / price : null;
            return (
              <div
                key={p.years}
                className="relative px-4 py-4 bg-slate-950/50 border border-white/10 rounded-xl"
              >
                {/* Horizon label + target date */}
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-orange-300">
                    {p.label}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono tabular-nums">
                    {format(p.date, 'MMM yyyy')}
                  </div>
                </div>

                {/* Fair value — the headline number for each tile */}
                <div className="text-2xl md:text-3xl font-bold tabular-nums text-orange-400 leading-tight">
                  {fmtCompact(p.fair)}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Fair-value target
                </div>

                {/* Floor / ceiling band — narrower text, in the model's color
                    language so they read as range, not as separate predictions */}
                <div className="mt-3 space-y-1 text-[11px] tabular-nums">
                  <div className="flex justify-between gap-2">
                    <span className="text-blue-300/80">Floor</span>
                    <span className="text-slate-300 font-mono">{fmtCompact(p.floor)}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-orange-300/80">Ceiling</span>
                    <span className="text-slate-300 font-mono">{fmtCompact(p.ceiling)}</span>
                  </div>
                </div>

                {/* "× from today" multiplier */}
                {fairMultiple && (
                  <div className="mt-2 pt-2 border-t border-white/5 text-[10px] text-slate-400">
                    <span className="text-emerald-300 font-bold tabular-nums">
                      {fairMultiple < 10
                        ? `${fairMultiple.toFixed(2)}×`
                        : `${Math.round(fairMultiple)}×`}
                    </span>{' '}
                    from today's price
                    {ceilingMultiple > fairMultiple && (
                      <span className="text-slate-500">
                        {' '}· up to{' '}
                        <span className="text-slate-300 font-mono tabular-nums">
                          {ceilingMultiple < 10
                            ? `${ceilingMultiple.toFixed(2)}×`
                            : `${Math.round(ceilingMultiple)}×`}
                        </span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-[10px] text-slate-500 italic mt-4 leading-relaxed">
          These projections assume the power-law continues to hold. They are
          NOT predictions — they are what the fitted model implies <em>if</em>
          its assumptions remain valid. Real outcomes always vary; check the
          model-confidence strip above to see how well the model has tracked
          reality so far.
          {fairToday > 0 && (
            <> Today's fair-value baseline:{' '}
              <strong className="text-slate-400 not-italic">{fmtCompact(fairToday)}</strong>.
            </>
          )}
        </p>
      </div>
    </section>
  );
}

function ProModeStats({ metrics, levels, model, modelVariant, showFormulas, setShowFormulas, now, price }) {
  return (
    <section className="bg-slate-900 border border-slate-800 rounded-2xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">Pro Mode — Live Math</h3>
        </div>
        <button
          onClick={() => setShowFormulas(s => !s)}
          className="text-xs px-3 py-1 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors flex items-center gap-1.5"
        >
          <Info className="w-3.5 h-3.5" />
          {showFormulas ? 'Hide formulas' : 'View Model Info'}
        </button>
      </div>

      {/*
        Multiplier labels are intentionally explicit about their basis.
        Earlier copy ("Fair-value multiplier", "Ceiling multiplier") was
        ambiguous — readers reasonably assumed those meant fair/floor or
        ceiling/floor, when the code computed them from the live price.
        Each label now spells out "from price → X" or "from floor → X" so
        the meaning matches the formula.
      */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
        <StatRow label="Days since genesis"            value={`${levels.days.toLocaleString()} days`} />
        <StatRow label="Genesis date"                  value={format(GENESIS, 'MMM d, yyyy')} />
        <StatRow label="Today"                         value={format(now, 'MMM d, yyyy HH:mm')} />
        <StatRow label="Model variant"                 value={modelVariant} />
        <StatRow label="Logarithmic position"          value={`${metrics.logPos.toFixed(2)}%`} />
        <StatRow label="Linear position"               value={`${metrics.linearPos.toFixed(2)}%`} />
        <StatRow label="Distance above floor"          value={`+${metrics.distanceToFloor.toFixed(1)}%`} />
        <StatRow label="Discount to fair value"        value={`${metrics.distanceToFair.toFixed(2)}%`} />
        <StatRow label="Price ÷ floor"                 value={`${(price / levels.floor).toFixed(2)}×`} />
        <StatRow label="Price → fair multiplier"       value={`${metrics.multiplierToFair.toFixed(2)}×`} />
        <StatRow label="Price → ceiling multiplier"    value={`${metrics.multiplierToCeiling.toFixed(2)}×`} />
        <StatRow label="Floor → ceiling channel width" value={`${(levels.ceiling / levels.floor).toFixed(2)}×`} />
        <StatRow label="Fair ÷ floor"                  value={`${(levels.fair / levels.floor).toFixed(2)}×`} />
      </div>

      {showFormulas && (
        <div className="mt-5 p-4 bg-slate-950 rounded-xl border border-slate-800 font-mono text-xs space-y-3">
          <div className="text-slate-500 uppercase tracking-widest text-[10px]">Live Variables</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CodeRow label="days"    value={levels.days.toLocaleString()} />
            <CodeRow label="floor"   value={fmtCompact(levels.floor)} />
            <CodeRow label="fair"    value={fmtCompact(levels.fair)} />
            <CodeRow label="ceiling" value={fmtCompact(levels.ceiling)} />
          </div>
          <div className="text-slate-500 uppercase tracking-widest text-[10px] pt-3 border-t border-slate-800">Formulas In Use</div>
          <div className="space-y-1.5">
            <div><span className="text-blue-400 font-semibold">Floor   = </span><span className="text-slate-300">10^({model.floor.coef.toFixed(3)}) × days^{model.floor.exp.toFixed(3)}</span></div>
            <div><span className="text-slate-300 font-semibold">Fair    = </span><span className="text-slate-300">10^({model.fair.coef.toFixed(3)}) × days^{model.fair.exp.toFixed(3)}</span></div>
            <div><span className="text-orange-400 font-semibold">Ceiling = </span><span className="text-slate-300">10^({model.ceiling.coef.toFixed(3)}) × days^{model.ceiling.exp.toFixed(3)}</span></div>
          </div>
          <div className="text-slate-500 pt-2 border-t border-slate-800">
            Substituting today's days = <span className="text-slate-300 font-bold">{levels.days.toLocaleString()}</span>:
            <div className="mt-1.5 space-y-0.5">
              <div>floor   = 10^{model.floor.coef.toFixed(2)}   × {levels.days}^{model.floor.exp}   = <span className="text-blue-400">{fmtCompact(levels.floor)}</span></div>
              <div>fair    = 10^{model.fair.coef.toFixed(2)}    × {levels.days}^{model.fair.exp}    = <span className="text-slate-200">{fmtCompact(levels.fair)}</span></div>
              <div>ceiling = 10^{model.ceiling.coef.toFixed(2)} × {levels.days}^{model.ceiling.exp} = <span className="text-orange-400">{fmtCompact(levels.ceiling)}</span></div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-800/60 last:border-0">
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-sm font-bold text-white tabular-nums">{value}</span>
    </div>
  );
}

function CodeRow({ label, value }) {
  return (
    <div className="bg-slate-900 rounded-md px-3 py-2 border border-slate-800">
      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-sm font-bold text-white tabular-nums">{value}</div>
    </div>
  );
}

// ─── Footer ─────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="text-center text-xs text-slate-500 pt-6 border-t border-slate-800/60 leading-relaxed">
      Power Law model based on the Santostasi / Burger framework. Live BTC price from CoinGecko (CoinCap fallback).{' '}
      <span className="text-slate-600">Educational use only — not financial advice.</span>
    </footer>
  );
}
